import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Chip, Button, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DownloadIcon from "@mui/icons-material/Download";
import SchoolIcon from "@mui/icons-material/School";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const TERMS = ["Term 1", "Term 2", "Term 3"];

const GRADE_CONFIG = {
  A:  { color: "#4caf50", bg: "#e8f5e9" },
  B:  { color: "#2196f3", bg: "#e3f2fd" },
  C:  { color: "#ff9800", bg: "#fff3e0" },
  D:  { color: "#f44336", bg: "#fdecea" },
  "A+": { color: "#4caf50", bg: "#e8f5e9" },
  "B+": { color: "#2196f3", bg: "#e3f2fd" },
};

const FALLBACK_STUDENT = {
  studentName: "Andrei Bloom",
  registrationNo: "STU2024001",
  grade: "Nilai 5",
  section: "A",
  teacherName: "Mrs. Kavitha Ravi",
  overallPercent: 84,
  overallGrade: "B",
  attendancePercent: 88,
  photo: null,
};

const FALLBACK_SUBJECTS = [
  { subject: "Tamil",   maxMarks: 100, marks: 85, grade: "A",  term: "Term 2", remarks: "Excellent reading and comprehension skills. Keep it up!" },
  { subject: "English", maxMarks: 100, marks: 78, grade: "B",  term: "Term 2", remarks: "Good writing skills. Focus on spoken English practice." },
  { subject: "Math",    maxMarks: 100, marks: 92, grade: "A+", term: "Term 2", remarks: "Outstanding performance in all chapters. Continue the good work." },
  { subject: "Science", maxMarks: 100, marks: 80, grade: "A",  term: "Term 2", remarks: "Good understanding of concepts. More practical exercises recommended." },
  { subject: "Social Studies", maxMarks: 100, marks: 74, grade: "B", term: "Term 2", remarks: "Satisfactory. Needs more focus on map work and history timelines." },
  { subject: "Computer", maxMarks: 100, marks: 88, grade: "A", term: "Term 2", remarks: "Great practical skills. Theory portion can be improved." },
];

const GradeChip = ({ grade }) => {
  const cfg = GRADE_CONFIG[grade] || { color: "#8F95B2", bg: "#f3f4f6" };
  return (
    <Chip
      label={grade}
      size="small"
      sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: "12px", minWidth: 36 }}
    />
  );
};

const SubjectRow = ({ row, idx }) => {
  const [open, setOpen] = useState(false);
  const pct = row.maxMarks > 0 ? Math.round((row.marks / row.maxMarks) * 100) : 0;

  return (
    <>
      <TableRow
        sx={{
          "&:hover": { backgroundColor: "#F9FAFB" },
          cursor: "pointer",
          backgroundColor: open ? "#F5F3FF" : "transparent",
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ py: 1.4, pl: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                backgroundColor: idx % 2 === 0 ? "#EDE9FB" : "#E3F2FD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SchoolIcon sx={{ fontSize: "16px", color: idx % 2 === 0 ? "#244C77" : "#2196f3" }} />
            </Box>
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#111827" }}>{row.subject}</Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.4 }}>
          {row.marks} / {row.maxMarks}
        </TableCell>
        <TableCell sx={{ py: 1.4 }}>
          <GradeChip grade={row.grade} />
        </TableCell>
        <TableCell sx={{ py: 1.4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 120 }}>
            <Box sx={{ flex: 1, height: 8, borderRadius: "4px", backgroundColor: "#E5E7EB", overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${pct}%`,
                  background: pct >= 85 ? "linear-gradient(90deg,#4caf50,#81c784)" : pct >= 70 ? "linear-gradient(90deg,#244C77,#4A7BA8)" : "linear-gradient(90deg,#ff9800,#ffb74d)",
                  borderRadius: "4px",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: "12px", color: "#6B7280", flexShrink: 0 }}>{pct}%</Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ fontSize: "12px", color: "#6B7280", py: 1.4 }}>{row.term || "—"}</TableCell>
        <TableCell sx={{ py: 1.4 }} align="right">
          <IconButton size="small" sx={{ color: "#4A7BA8" }}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: 3,
                py: 1.5,
                backgroundColor: "#F5F3FF",
                borderLeft: "4px solid #4A7BA8",
                mx: 1,
                mb: 1,
                borderRadius: "0 8px 8px 0",
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#244C77", mb: 0.4 }}>
                Teacher's Remarks
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#374151" }}>
                {row.remarks || "No remarks added for this subject."}
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ParentProgressReport = () => {
  const [termTab, setTermTab] = useState(1);
  const [reportData, setReportData] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadReport = useCallback(async () => {
    try {
      const term = TERMS[termTab].replace(" ", "_").toLowerCase();
      const res = await fetchData(`parent/progress-report?term=${term}`);
      if (res?.data) {
        setReportData(res.data);
      }
    } catch (_) {
      // Fallback data will be used
    }
  }, [termTab]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const student = reportData?.student || FALLBACK_STUDENT;
  const subjects = reportData?.subjects || FALLBACK_SUBJECTS;

  const initials = student.studentName
    ? student.studentName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "AB";

  const handleExport = () => window.print();

  return (
    <Box sx={{ textAlign: "left", padding: "1rem", fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 1.5 }}>
        Pages / Progress Report
      </Typography>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>

        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #F3F4F6" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "18px", color: "#111827" }}>
                  Progress Report Card
                </Typography>
                <Chip
                  label={`Section ${student.section || "A"}`}
                  size="small"
                  sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: 600, fontSize: "12px" }}
                />
              </Box>
              <Typography sx={{ fontSize: "13px", color: "#6B7280", mt: 0.5 }}>
                Academic performance for your child
              </Typography>
            </Box>
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: "16px" }} />}
              onClick={handleExport}
              sx={{
                border: "1px solid #D5D7DA",
                borderRadius: "8px",
                color: "#374151",
                textTransform: "none",
                fontSize: "13px",
                px: 2,
                py: 0.8,
                "&:hover": { backgroundColor: "#F9FAFB" },
              }}
            >
              Export
            </Button>
          </Box>

          {/* Term tabs */}
          <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
            {TERMS.map((term, idx) => (
              <Button
                key={term}
                onClick={() => setTermTab(idx)}
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.6,
                  borderRadius: "20px",
                  backgroundColor: termTab === idx ? "#244C77" : "transparent",
                  color: termTab === idx ? "#fff" : "#6B7280",
                  "&:hover": { backgroundColor: termTab === idx ? "#244C77" : "#F3F4F6" },
                  minWidth: "unset",
                }}
              >
                {term}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Student banner */}
        <Box
          sx={{
            mx: 3,
            mt: 2.5,
            mb: 2.5,
            borderRadius: "14px",
            overflow: "hidden",
            background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={student.photo || undefined}
              sx={{
                width: 68,
                height: 68,
                border: "3px solid rgba(255,255,255,0.4)",
                fontSize: "22px",
                fontWeight: "bold",
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "#fff",
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "18px", lineHeight: 1.3 }}>
                {student.studentName || "Student Name"}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12.5px", mt: 0.4 }}>
                {student.grade || "Nilai 5"} &nbsp;·&nbsp; Sec {student.section || "A"} &nbsp;·&nbsp; {TERMS[termTab]} &nbsp;·&nbsp;
                Teacher: {student.teacherName || "—"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {[
              { value: `${student.overallPercent ?? 84}%`, label: "Overall" },
              { value: student.overallGrade || "B", label: "Grade" },
              { value: `${student.attendancePercent ?? 88}%`, label: "Attendance" },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  backgroundColor: "rgba(0,0,0,0.28)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1.2,
                  textAlign: "center",
                  minWidth: 72,
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "20px", lineHeight: 1.2 }}>
                  {stat.value}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "11px", mt: 0.2 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Subjects table */}
        <TableContainer sx={{ px: 1, pb: 0 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                {["Subject", "Marks", "Grade", "Progress", "Term", ""].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#6B7280",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.2,
                      whiteSpace: "nowrap",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {subjects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: "#9CA3AF", fontSize: "13px" }}>
                    No progress data available for {TERMS[termTab]}
                  </TableCell>
                </TableRow>
              ) : (
                subjects.map((row, idx) => (
                  <SubjectRow key={idx} row={row} idx={idx} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Teacher overall comment */}
        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#374151", mb: 1 }}>
            Overall Teacher's Comments
          </Typography>
          <Box
            sx={{
              borderRadius: "10px",
              border: "1px solid #E5E7EB",
              backgroundColor: "#F9FAFB",
              px: 2.5,
              py: 2,
              minHeight: "72px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: reportData?.teacherComments ? "#111827" : "#9CA3AF",
                fontStyle: reportData?.teacherComments ? "normal" : "italic",
              }}
            >
              {reportData?.teacherComments || "Overall, the student is showing good progress. Continued effort in all subjects is encouraged."}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ParentProgressReport;
